import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Save, Loader2, ChevronDown, Info, Settings, Cpu } from "lucide-react";
import { machinesAPI } from "../api";
import toast from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";
import clsx from "clsx";

const statusOptionsConfig = {
  operational: {
    label: "Operational",
    dot: "bg-emerald-500",
  },
  maintenance: {
    label: "In Maintenance",
    dot: "bg-amber-500",
  },
  error: {
    label: "Error",
    dot: "bg-rose-500",
  },
  offline: {
    label: "Offline",
    dot: "bg-slate-400",
  },
};

const MachineConfigModal = ({ isOpen, onClose, machine }) => {
  const queryClient = useQueryClient();
  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const statusRef = useRef(null);
  const [formData, setFormData] = useState({
    name: "",
    status: "operational",
  });

  useEffect(() => {
    if (machine) {
      setFormData({
        name: machine.name || "",
        status: machine.status || "operational",
      });
    }
  }, [machine, isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (statusRef.current && !statusRef.current.contains(event.target)) {
        setIsStatusOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const updateMutation = useMutation({
    mutationFn: (data) => machinesAPI.update(machine.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["machine", machine?.id] });
      queryClient.invalidateQueries({ queryKey: ["machines"] });
      toast.success("Machine configuration updated");
      onClose();
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.error || "Failed to update configuration"
      );
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error("Machine name is required");
      return;
    }
    updateMutation.mutate(formData);
  };

  if (!isOpen) return null;

  return createPortal(
    <AnimatePresence>
      <div
        className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-4 bg-slate-950/70 backdrop-blur-sm overflow-y-auto"
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 10 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          className="relative w-full max-w-lg bg-white rounded-2xl border border-slate-200 shadow-2xl overflow-hidden flex flex-col my-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-5 sm:p-6 border-b border-slate-100 bg-white">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 border border-blue-100/80 flex items-center justify-center flex-shrink-0">
                <Settings className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-base sm:text-lg font-bold text-slate-900 tracking-tight">
                  Configure Machine
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  Update operating status and machine telemetry settings
                </p>
              </div>
            </div>
          </div>

          {/* Form Body */}
          <form onSubmit={handleSubmit} className="flex flex-col">
            <div className="p-5 sm:p-6 space-y-4 sm:space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Machine Name */}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                    Machine Name <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative">
                    <Cpu className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      placeholder="e.g. CNC Milling Center 02"
                      className="w-full pl-10 pr-3.5 py-2.5 bg-slate-50/60 hover:bg-slate-50 focus:bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all shadow-sm"
                    />
                  </div>
                </div>

                {/* Status Dropdown */}
                <div className="relative" ref={statusRef}>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                    Operating Status
                  </label>
                  <button
                    type="button"
                    onClick={() => setIsStatusOpen(!isStatusOpen)}
                    className="w-full pl-3.5 pr-3.5 py-2.5 bg-slate-50/60 hover:bg-slate-50 focus:bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 flex items-center justify-between transition-all shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className={clsx(
                          "w-2 h-2 rounded-full",
                          statusOptionsConfig[formData.status]?.dot || "bg-slate-400"
                        )}
                      />
                      <span className="font-medium text-slate-800">
                        {statusOptionsConfig[formData.status]?.label || "Select Status"}
                      </span>
                    </div>
                    <ChevronDown
                      className={clsx(
                        "w-4 h-4 text-slate-400 transition-transform duration-200",
                        isStatusOpen && "rotate-180"
                      )}
                    />
                  </button>

                  <AnimatePresence>
                    {isStatusOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: -6, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -6, scale: 0.98 }}
                        transition={{ duration: 0.15 }}
                        className="absolute z-20 mt-1.5 w-full rounded-xl bg-white shadow-xl border border-slate-200 py-1.5"
                      >
                        {Object.entries(statusOptionsConfig).map(([key, config]) => (
                          <button
                            type="button"
                            key={key}
                            onClick={() => {
                              setFormData({ ...formData, status: key });
                              setIsStatusOpen(false);
                            }}
                            className={clsx(
                              "flex items-center justify-between w-full px-3.5 py-2.5 text-xs text-left transition-colors font-medium",
                              formData.status === key
                                ? "bg-blue-50 text-blue-700 font-semibold"
                                : "text-slate-700 hover:bg-slate-50"
                            )}
                          >
                            <div className="flex items-center gap-2">
                              <span className={clsx("w-2 h-2 rounded-full", config.dot)} />
                              <span>{config.label}</span>
                            </div>
                            {formData.status === key && (
                              <span className="w-1.5 h-1.5 rounded-full bg-blue-600" />
                            )}
                          </button>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>

              {/* Info Note */}
              <div className="bg-blue-50/70 border border-blue-100 rounded-xl p-3.5 flex items-start gap-3">
                <Info className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
                <p className="text-xs text-blue-800 leading-relaxed">
                  Updating core machine metadata will affect live telemetry feeds and status logs linked to this equipment ID.
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 sm:p-5 bg-slate-50/80 border-t border-slate-100 flex items-center justify-end gap-2.5">
              <button
                type="button"
                onClick={onClose}
                className="btn btn-secondary text-xs sm:text-sm py-2 px-3.5 sm:px-4 hover:border-slate-300"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={updateMutation.isPending}
                className="btn btn-primary text-xs sm:text-sm py-2 px-4 shadow-md shadow-blue-500/20 whitespace-nowrap"
              >
                {updateMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-1.5" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-1.5" />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>,
    document.body
  );
};

export default MachineConfigModal;
